import React, { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';

const UserProfile = () => {
    const { user, login } = useAuth();
    const { showToast } = useUI();

    const [passwords, setPasswords] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [avatarPreview, setAvatarPreview] = useState(user?.avatar || null);
    const fileInputRef = useRef(null);
    const [loading, setLoading] = useState(false);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) { // 2MB limit
                showToast("L'image est trop volumineuse (max 2Mo)", "error");
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setAvatarPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSaveProfile = async () => {
        if (passwords.newPassword && passwords.newPassword !== passwords.confirmPassword) {
            showToast("Les nouveaux mots de passe ne correspondent pas.", "error");
            return;
        }

        if (passwords.newPassword && !passwords.currentPassword) {
            showToast("Veuillez saisir votre mot de passe actuel pour le modifier.", "error");
            return;
        }

        try {
            setLoading(true);
            const payload = {
                name: user.name,
                email: user.email,
                roleId: user.roleId,
                status: user.status,
                avatar: avatarPreview
            };

            if (passwords.newPassword) {
                payload.password = passwords.newPassword;
                payload.currentPassword = passwords.currentPassword;
            }

            const response = await fetch(`/api/users/${user.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Erreur lors de la mise à jour');
            }

            // Reconstruire l'objet user complet pour le contexte
            const updatedUser = {
                ...user,
                avatar: avatarPreview
            };

            const isRemembered = localStorage.getItem('stock_user') !== null;
            login(updatedUser, isRemembered);

            showToast("Profil mis à jour avec succès", "success");
            setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error) {
            showToast(error.message || "Erreur lors de la mise à jour du profil", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-background-light dark:bg-background-dark">
            {/* Header Sticky */}
            <div className="sticky top-[-16px] lg:top-[-16px] z-30 bg-background-light/95 dark:bg-background-dark/95 backdrop-blur-sm pt-4 pb-6 mb-2 border-b border-slate-200/50 dark:border-slate-800/50 transition-all duration-200">
                <div className="flex flex-wrap justify-between items-end gap-4 max-w-[1600px] mx-auto w-full px-6">
                    <div className="flex flex-col gap-1">
                        <h1 className="text-3xl font-black leading-tight tracking-tight flex items-center gap-3 bg-gradient-to-r from-primary to-blue-500 bg-clip-text text-transparent drop-shadow-sm">
                            <span className="material-symbols-outlined text-primary text-3xl">person</span>
                            Mon Profil
                        </h1>
                        <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Gérez vos informations personnelles et vos paramètres de sécurité.</p>
                    </div>
                </div>
            </div>

            {/* Contenu Principal */}
            <div className="flex-1 w-full max-w-[1600px] mx-auto px-6 pb-6">
                <div className="bg-white dark:bg-slate-900/50 rounded-3xl border border-slate-200 dark:border-slate-800 overflow-hidden backdrop-blur-sm shadow-xl shadow-slate-200/50 dark:shadow-none p-6 md:p-8">
                    <div className="flex flex-col md:flex-row gap-8 items-start">

                        {/* Avatar Section */}
                        <div className="flex flex-col items-center gap-4">
                            <div className="relative size-32 rounded-full overflow-hidden bg-slate-100 dark:bg-slate-800 border-4 border-white dark:border-slate-900 shadow-xl group">
                                {avatarPreview ? (
                                    <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                                ) : (
                                    <span className="w-full h-full flex items-center justify-center text-4xl font-bold text-slate-400 uppercase">
                                        {user?.name?.charAt(0)}
                                    </span>
                                )}
                                <div
                                    className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <span className="material-symbols-outlined text-white text-3xl">photo_camera</span>
                                </div>
                            </div>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                accept="image/*"
                                className="hidden"
                            />
                            {avatarPreview && (
                                <button
                                    onClick={() => setAvatarPreview(null)}
                                    className="text-sm text-rose-500 font-bold hover:underline"
                                >
                                    Supprimer la photo
                                </button>
                            )}
                        </div>

                        {/* Informations Section */}
                        <div className="flex-1 w-full space-y-6">
                            <div>
                                <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-wider mb-4">Informations</h3>
                                <div className="grid grid-cols-1 gap-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-500 mb-1">Nom</label>
                                        <input type="text" value={user?.name || ''} disabled className="w-full bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-2.5 text-sm text-slate-500 cursor-not-allowed" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-500 mb-1">Email</label>
                                        <input type="email" value={user?.email || ''} disabled className="w-full bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-2.5 text-sm text-slate-500 cursor-not-allowed" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-500 mb-1">Rôle</label>
                                        <input type="text" value={user?.role?.name || ''} disabled className="w-full bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-2.5 text-sm font-bold text-primary cursor-not-allowed" />
                                    </div>
                                </div>
                            </div>

                            <div className="border-t border-slate-200 dark:border-slate-800 pt-6">
                                <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-wider mb-4">Sécurité</h3>
                                <div className="grid grid-cols-1 gap-4">
                                    {passwords.newPassword && (
                                        <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                            <label className="block text-xs font-semibold text-rose-500 mb-1">Mot de passe actuel (Requis pour vérification)</label>
                                            <input
                                                type="password"
                                                value={passwords.currentPassword}
                                                onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
                                                placeholder="••••••••"
                                                className="w-full bg-white dark:bg-slate-900 border border-rose-200 dark:border-rose-900/50 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-rose-500 outline-none"
                                            />
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-500 mb-1">Nouveau mot de passe</label>
                                        <input
                                            type="password"
                                            value={passwords.newPassword}
                                            onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                                            placeholder="Laisser vide pour ne pas modifier"
                                            className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-500 mb-1">Confirmer le nouveau mot de passe</label>
                                        <input
                                            type="password"
                                            value={passwords.confirmPassword}
                                            onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                                            className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary outline-none"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="pt-4 flex justify-end">
                                <button
                                    onClick={handleSaveProfile}
                                    disabled={loading}
                                    className="px-6 py-2.5 bg-primary text-white text-sm font-bold rounded-xl hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg shadow-primary/20 disabled:opacity-50 flex items-center gap-2"
                                >
                                    {loading ? (
                                        <span className="material-symbols-outlined animate-spin text-[20px]">refresh</span>
                                    ) : (
                                        <span className="material-symbols-outlined text-[20px]">save</span>
                                    )}
                                    Enregistrer les modifications
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserProfile;
